/**
 * "Add from contacts" on sign-up's Add your team step: in the Android app, Android's own contact
 * picker opens, the owner taps one person's number, and only that name and number come back.
 * Native plugin: `native-android/ContactPickerPlugin.java` (Intent.ACTION_PICK on the phone-number
 * list, which grants a one-time read of the chosen row). No READ_CONTACTS permission: the app never
 * reads the address book. A browser has no contacts, so this is app-only; an older installed app has
 * no plugin, and the step then shows its typed rows instead of the button.
 */

import { App } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';

interface ContactPickerPlugin {
  pick(): Promise<{ name?: string; phone?: string; cancelled?: boolean; unavailable?: string }>;
}

/** Registered on first use, so merely importing this file touches no native bridge. */
let plugin: ContactPickerPlugin | null = null;
const contactPickerPlugin = (): ContactPickerPlugin =>
  (plugin ??= registerPlugin<ContactPickerPlugin>('ContactPicker'));

/** Set once the phone answers it has no contacts app: the button then stays hidden. */
let noPickerOnPhone = false;

export function contactPickerAvailable(): boolean {
  try {
    return (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('ContactPicker') &&
      !noPickerOnPhone
    );
  } catch {
    return false;
  }
}

/** One picked contact: the number as the phone stores it (E.164 when Android could tell). */
export interface PickedContact {
  name: string;
  phone: string;
}

type PickResult = Awaited<ReturnType<ContactPickerPlugin['pick']>>;

/** The plugin's answer as a contact, or null for a closed picker or an entry with no number. */
function toPickedContact(result: PickResult | undefined): PickedContact | null {
  const phone = typeof result?.phone === 'string' ? result.phone.trim() : '';
  if (!phone) return null;
  const name = typeof result?.name === 'string' ? result.name.trim() : '';
  return { name, phone };
}

/**
 * The contact the person chose; null when they closed the picker, chose an entry with no number,
 * or the picker cannot be shown. Never throws.
 */
export async function pickContact(): Promise<PickedContact | null> {
  if (!contactPickerAvailable()) return null;
  try {
    const result = await contactPickerPlugin().pick();
    if (result?.unavailable) {
      noPickerOnPhone = true;
      return null;
    }
    return toPickedContact(result);
  } catch {
    return null;
  }
}

/**
 * A contact picked while the phone killed the app. Low-memory phones (MIUI especially) end the app
 * as soon as Contacts is in front; Capacitor then restarts it and hands the picker's answer to
 * @capacitor/app's `appRestoredResult` (retained until someone listens). Held here until the team
 * screen takes it.
 */
let restoredContact: PickedContact | null = null;
const restoredWaiters = new Set<(contact: PickedContact) => void>();

/**
 * Listens once per process, from app start (NativeLinkRouting): `onRestored` runs when a picked
 * contact came back after a restart, so the caller can return to the team screen.
 */
let restoreListening = false;
export function listenForRestoredContact(onRestored: () => void): void {
  if (restoreListening || !Capacitor.isNativePlatform()) return;
  restoreListening = true;
  App.addListener('appRestoredResult', (event) => {
    if (event.pluginId !== 'ContactPicker' || event.methodName !== 'pick' || !event.success) return;
    const contact = toPickedContact(event.data as PickResult | undefined);
    if (!contact) return;
    if (restoredWaiters.size > 0) {
      // Every currently-registered waiter, not just one: an overlapping remount can briefly leave
      // more than one subscribed before an earlier effect's cleanup runs.
      for (const waiter of restoredWaiters) waiter(contact);
    } else {
      restoredContact = contact;
    }
    onRestored();
  }).catch(() => {
    // No App plugin (an older shell): a pick lost to a restart is simply lost, as before.
  });
}

/**
 * The team screen's side: `onContact` gets a contact picked before a restart — at once when it is
 * already here, or when it arrives. Returns the unsubscribe.
 */
export function takeRestoredContact(onContact: (contact: PickedContact) => void): () => void {
  if (restoredContact) {
    const contact = restoredContact;
    restoredContact = null;
    onContact(contact);
  }
  restoredWaiters.add(onContact);
  return () => {
    restoredWaiters.delete(onContact);
  };
}

/** Tests only: forget a "no contacts app" answer and any restored contact. */
export function resetContactPickerForTests(): void {
  noPickerOnPhone = false;
  plugin = null;
  restoredContact = null;
  restoredWaiters.clear();
  restoreListening = false;
}
